<script setup lang="ts">
// Métadonnées de la page d'inscription
import { z } from 'zod'

useHead({
  title: 'S\'inscrire - Glouton',
  meta: [
    { name: 'description', content: 'Créez votre compte Glouton gratuitement et accédez à toutes nos fonctionnalités pour optimiser vos courses.' },
    { property: 'og:title', content: 'S\'inscrire - Glouton' },
    { property: 'og:description', content: 'Créez votre compte Glouton gratuitement et accédez à toutes nos fonctionnalités pour optimiser vos courses.' },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/register',
    fr: '/inscription',
  },
})

definePageMeta({
  name: 'RegisterPage',
})

const userStore = useUserStore()
const consentStore = useConsentStore()
const config = useRuntimeConfig()
const toast = useToast()
const baseStore = useBaseStore()
const modalStore = useModalStore()

// Form data
const form = ref({
  firstname: '',
  lastname: '',
  email: '',
  postalcode: '',
  password: '',
  password_confirmation: '',
  stores: [] as string[],
  email_alerts: false,
  email_intro: false,
})

// Form state
const isRegisterLoading = ref(false)
const isSuccess = ref(false)
const email = ref('')
const selectedPlan = ref('extra')
const selectedStores = ref<string[]>([])
const showMoreStores = ref(false)

// Check if email is from slow domain (sympatico, hotmail, videotron)
const emailWaitTime = computed(() => {
  const slowDomains = ['sympatico', 'hotmail', 'videotron']
  const emailLower = email.value.toLowerCase()
  const isSlowDomain = slowDomains.some(domain => emailLower.includes(domain))
  return isSlowDomain ? '15 minutes' : 'quelques minutes'
})

// Validation errors
const errors = ref<Record<string, string>>({})

// Register email hint (API, hors schéma Zod)
const emailRegisteredHint = ref(false)
const lastCheckedEmail = ref('')
let emailHintRequestId = 0

// Zod validation schema
const baseSchema = z.object({
  firstname: z.string().min(1, 'Le prénom est requis'),
  lastname: z.string().min(1, 'Le nom de famille est requis'),
  email: z.string().email('Adresse courriel invalide'),
  postalcode: z.string().regex(/([A-Z]\d[A-Z][ -]?\d[A-Z]\d)/i, 'Code postal canadien invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  password_confirmation: z.string(),
  stores: z.array(z.string()).min(1, 'Sélectionnez au moins un supermarché'),
  email_alerts: z.boolean(),
  email_intro: z.boolean(),
})

const registerSchema = baseSchema.refine(data => data.password === data.password_confirmation, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['password_confirmation'],
})

// Stores data
const stores = [
  { id: 1, name: 'Metro', slug: 'metro', base: true },
  { id: 2, name: 'Super C', slug: 'superc', base: true },
  { id: 3, name: 'IGA', slug: 'iga', base: true },
  { id: 5, name: 'Maxi', slug: 'maxi', base: true },
  { id: 6, name: 'Walmart', slug: 'walmart', base: true },
  { id: 4, name: 'Provigo', slug: 'provigo', base: true },
  { id: 15, name: 'Tigre Géant', slug: 'tigre-geant', base: true },
  { id: 9, name: 'Adonis', slug: 'adonis', base: true },
  { id: 19, name: 'Jean Coutu', slug: 'jean-coutu', base: false },
  { id: 27, name: 'Brunet', slug: 'brunet', base: false },
  { id: 22, name: 'Familiprix', slug: 'familiprix', base: false },
  { id: 20, name: 'Uniprix', slug: 'uniprix', base: false },
  { id: 18, name: 'Pharmaprix', slug: 'pharmaprix', base: false },
]

// Plans data
const plans = [
  {
    id: 1,
    type: 'extra',
    title: 'Extra',
    description: 'Comprend les fonctionnalités gratuites ainsi que:',
    features: [
      { checked: true, description: 'L\'économiseur: recettes avantageuses selon les spéciaux' },
      { checked: true, description: 'Boîte de recettes: classez et importez des recettes de n\'importe quel site Web' },
      { checked: true, description: 'Planificateur: calendrier de gestion des repas' },
      { checked: true, description: 'Suivez les aubaines de vos ingrédients favoris' },
      { checked: true, description: 'Filtrez les rabais par allée dans les circulaires et les meilleurs rabais' },
      { checked: true, description: 'Classez automatiquement votre liste d\'épicerie par allée' },
      { checked: true, description: 'Accès à tous les rabais chez nos partenaires' },
      { no_ads: true, description: 'Aucune publicité' },
    ],
  },
  {
    id: 2,
    type: 'free',
    title: 'Gratuit',
    description: 'Fonctionnalités limitées pour une utilisation de base.',
    features: [
      { checked: true, description: 'Circulaires: trouvez les vrais spéciaux qui valent la peine' },
      { checked: true, description: 'Liste d\'épicerie: moderne, simple et performant' },
      { checked: true, description: 'Vide-frigo: cuisinez sans gaspiller' },
      { checked: true, description: 'Filtres: allergies et restrictions alimentaires' },
      { checked: true, description: 'Accès à plusieurs rabais chez nos partenaires' },
      { ads: true, description: 'Supporté par la publicité' },
    ],
  },
]

// Methods
function registerLink(source: string) {
  // Analytics tracking
  if (typeof plausible !== 'undefined') {
    plausible('register_social', { props: { name: source } })
  }
  if (consentStore.googleAnalyticsAllowed && typeof window !== 'undefined') {
   ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push({
      event: 'register_social_click',
      app_origin: source,
    })
  }
}

// Validation methods
function validateField(fieldName: string, value: any) {
  try {
    // Create a partial schema for the specific field
    const fieldSchema = z.object({
      [fieldName]: baseSchema.shape[fieldName as keyof typeof baseSchema.shape],
    })

    fieldSchema.parse({ [fieldName]: value })

    // Special validation for password confirmation
    if (fieldName === 'password_confirmation' && form.value.password) {
      if (value !== form.value.password) {
        errors.value[fieldName] = 'Les mots de passe ne correspondent pas'
        return
      }
    }

    // Clear error if validation passes
    if (errors.value[fieldName]) {
      delete errors.value[fieldName]
    }
  }
  catch (error) {
    if (error instanceof z.ZodError) {
      const fieldError = error.errors.find(err => err.path[0] === fieldName)
      if (fieldError) {
        errors.value[fieldName] = fieldError.message
      }
    }
  }
}

function validateStores() {
  try {
    const storesSchema = z.object({
      stores: baseSchema.shape.stores,
    })

    storesSchema.parse({ stores: selectedStores.value })

    if (errors.value.stores) {
      delete errors.value.stores
    }
  }
  catch (error) {
    if (error instanceof z.ZodError) {
      const storesError = error.errors.find(err => err.path[0] === 'stores')
      if (storesError) {
        errors.value.stores = storesError.message
      }
    }
  }
}

function validatePasswordConfirmation() {
  // Validate password confirmation when password changes
  if (form.value.password_confirmation && form.value.password) {
    if (form.value.password_confirmation !== form.value.password) {
      errors.value.password_confirmation = 'Les mots de passe ne correspondent pas'
    }
    else {
      delete errors.value.password_confirmation
    }
  }
}

function onEmailInput() {
  emailRegisteredHint.value = false
  lastCheckedEmail.value = ''
}

async function onEmailBlur() {
  validateField('email', form.value.email)

  if (errors.value.email) {
    emailRegisteredHint.value = false
    return
  }

  if (form.value.email === lastCheckedEmail.value) {
    return
  }

  const requestId = ++emailHintRequestId

  try {
    const response = await $fetch<{ success: number, email_registered?: boolean }>(`${config.public.apiUrl}/auth/register-email-hint`, {
      method: 'POST',
      body: { email: form.value.email },
    })

    if (requestId !== emailHintRequestId) {
      return
    }

    lastCheckedEmail.value = form.value.email
    emailRegisteredHint.value = response.email_registered === true
  }
  catch {
    // Ne pas afficher d'erreur (pas de fuite d'info)
  }
}

function openForgotPassword() {
  modalStore.openLoginModal({ forgotPassword: true, email: form.value.email })
}

async function handleSubmit() {
  try {
    // Clear previous errors
    errors.value = {}

    // Validate form
    const validatedData = registerSchema.parse({
      ...form.value,
      stores: selectedStores.value,
    })

    isRegisterLoading.value = true

    const response = await $fetch(`${config.public.apiUrl}/auth/register`, {
      method: 'POST',
      body: {
        firstname: validatedData.firstname,
        lastname: validatedData.lastname,
        email: validatedData.email,
        postalcode: validatedData.postalcode,
        password: validatedData.password,
        password_confirmation: validatedData.password_confirmation,
        stores: validatedData.stores,
        email_alerts: validatedData.email_alerts,
        email_intro: validatedData.email_intro,
        plan: selectedPlan.value,
      },
    })

    isRegisterLoading.value = false

    if (response.success === 1) {
      // Success
      isSuccess.value = true
      email.value = validatedData.email

      // Analytics tracking
      if (typeof plausible !== 'undefined') {
        plausible('signup')
      }
      if (consentStore.advertisingAllowed && typeof fbq !== 'undefined') {
        fbq('track', 'CompleteRegistration', {}, { eventID: response.event_id })
      }
      if ((consentStore.googleAnalyticsAllowed || consentStore.advertisingAllowed) && typeof window !== 'undefined') {
        ;(window as any).dataLayer = (window as any).dataLayer || []
        ;(window as any).dataLayer.push({ event: 'sign_up' })
      }
      if (typeof $crisp !== 'undefined') {
        $crisp.push(['set', 'session:event', [[
          ['account_created', {}, 'blue'],
        ]]])
      }
      if (consentStore.advertisingAllowed && typeof ttq !== 'undefined' && typeof ttq.track === 'function') {
        ttq.track('CompleteRegistration', {
          registration_method: 'Email',
          value: 0.00,
          currency: 'CAD',
          event_id: response.event_id,
        })
      }

      if (selectedPlan.value === 'extra') {
        if (consentStore.advertisingAllowed && typeof fbq !== 'undefined') {
          fbq('track', 'StartTrial', { value: '0.00', currency: 'CAD', predicted_ltv: '0.00' })
        }
        if ((consentStore.googleAnalyticsAllowed || consentStore.advertisingAllowed) && typeof window !== 'undefined') {
          ;(window as any).dataLayer = (window as any).dataLayer || []
          ;(window as any).dataLayer.push({ event: 'start_free_trial' })
        }
      }
    }
    else if (response.success === 0) {
      toast.add({
        title: 'Erreur',
        description: response.message,
        color: 'error',
      })
    }
    else {
      toast.add({
        title: 'Erreur',
        description: 'Une erreur est survenue lors du traitement de votre demande.',
        color: 'error',
      })
    }
  }
  catch (error) {
    isRegisterLoading.value = false

    if (error instanceof z.ZodError) {
      // Map Zod errors to field names
      error.errors.forEach((err) => {
        const field = err.path[0] as string
        errors.value[field] = err.message
      })

      // Show first error in toast
      const firstError = error.errors[0]
      if (firstError) {
        toast.add({
          title: 'Erreur de validation',
          description: firstError.message,
          color: 'error',
        })
      }
    }
    else {
      toast.add({
        title: 'Erreur',
        description: 'Une erreur est survenue lors du traitement de votre demande.',
        color: 'error',
      })
    }
  }
}

// SEO
useHead({
  title: 'Inscription - Glouton',
  meta: [
    { name: 'description', content: 'Créez votre compte Glouton pour maximiser vos économies à l\'épicerie.' },
    { property: 'og:title', content: 'Inscription - Glouton' },
    { property: 'og:description', content: 'Créez votre compte Glouton pour maximiser vos économies à l\'épicerie.' },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
  ],
  script: [
    {
      key: 'fb-js-sdk',
      src: 'https://connect.facebook.net/fr_CA/sdk.js#xfbml=1&version=v12.0&appId=774021859466667&autoLogAppEvents=1',
      nonce: 'pSrNApqJ',
      crossorigin: 'anonymous',
      async: true,
      defer: true,
    },
  ],
})

// Initialize
onMounted(() => {
  document.querySelector('body')?.scrollIntoView({ behavior: 'smooth' })
})
</script>

<template>
  <main class="relative flex-1 overflow-y-auto focus:outline-none">
    <TopNav
      title="Créer un compte"
      description="L'inscription est gratuite et permet d'utiliser de nombreuses fonctionnalités de Glouton."
      class="mx-auto max-w-7xl w-full"
    />

    <!-- Success State -->
    <div v-show="isSuccess" class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div class="relative rounded-lg overflow-hidden">
        <div class="absolute inset-0" />
        <div class="relative bg-sauge-500 py-16 md:py-32 px-6 sm:py-40 sm:px-12 lg:px-16">
          <div class="relative max-w-3xl mx-auto flex flex-col items-center text-center">
            <div>
              <UIcon name="i-mdi-check-circle" class="text-green-500 size-12" />
            </div>
            <h2 class="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              <span class="block sm:inline">Presque terminé !</span>
            </h2>
            <p class="mt-3 text-xl text-white">
              Votre compte a été créé avec succès ! Un courriel de confirmation a été envoyé à <strong>{{ email }}</strong>.
              Vous devez cliquer sur le lien dans ce message pour activer votre compte.<br><br>
              Si vous n'avez pas reçu le courriel d'ici {{ emailWaitTime }},
              <a href="#" class="underline" @click.prevent="baseStore.openCrispChat">contactez-nous</a>.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Registration Form -->
    <form v-show="!isSuccess" class="px-4 mx-auto max-w-7xl sm:px-6 md:px-8 mt-5 mb-20" @submit.prevent="handleSubmit">
      <UCard class="my-5">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-bold leading-6 text-poivre-800">
              Profil
            </h3>
            <p class="mt-1 text-sm text-poivre-500">
              Les informations de base et accès à votre compte
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="grid grid-cols-6 gap-6">
              <div class="col-span-6 sm:col-span-3">
                <label for="firstname" class="block text-base font-medium text-poivre-700">
                  Prénom
                </label>
                <UInput
                  v-model="form.firstname"
                  type="text"
                  autocomplete="given-name"
                  class="w-full"
                  size="lg"
                  :error="errors.firstname"
                  @blur="validateField('firstname', form.firstname)"
                />
                <p v-if="errors.firstname" class="mt-2 text-sm text-red-500">
                  {{ errors.firstname }}
                </p>
              </div>

              <div class="col-span-6 sm:col-span-3">
                <label for="lastname" class="block text-base font-medium text-poivre-700">
                  Nom de famille
                </label>
                <UInput
                  v-model="form.lastname"
                  type="text"
                  autocomplete="family-name"
                  class="w-full"
                  size="lg"
                  :error="errors.lastname"
                  @blur="validateField('lastname', form.lastname)"
                />
                <p v-if="errors.lastname" class="mt-2 text-sm text-red-500">
                  {{ errors.lastname }}
                </p>
              </div>

              <div class="col-span-6 sm:col-span-4">
                <label for="email" class="block text-base font-medium text-poivre-700">
                  Adresse courriel
                </label>
                <UInput
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  class="w-full"
                  size="lg"
                  :error="errors.email || emailRegisteredHint"
                  @blur="onEmailBlur"
                  @input="onEmailInput"
                />
                <p v-if="errors.email" class="mt-2 text-sm text-red-500">
                  {{ errors.email }}
                </p>
                <p v-else-if="emailRegisteredHint" class="mt-2 text-sm text-red-500">
                  Un compte existe déjà avec cette adresse.
                  <button type="button" class="underline font-medium" @click="openForgotPassword">
                    Mot de passe oublié?
                  </button>
                </p>
              </div>

              <div class="col-span-6 sm:col-span-3">
                <label for="password" class="block text-base font-medium text-poivre-700">
                  Mot de passe
                </label>
                <UInput
                  v-model="form.password"
                  type="password"
                  autocomplete="new-password"
                  class="w-full"
                  size="lg"
                  :error="errors.password"
                  @blur="validateField('password', form.password)"
                  @input="validatePasswordConfirmation"
                />
                <p v-if="errors.password" class="mt-2 text-sm text-red-500">
                  {{ errors.password }}
                </p>
              </div>

              <div class="col-span-6 sm:col-span-3">
                <label for="password_confirmation" class="block text-base font-medium text-poivre-700">
                  Confirmez le mot de passe
                </label>
                <UInput
                  v-model="form.password_confirmation"
                  type="password"
                  autocomplete="new-password"
                  class="w-full"
                  size="lg"
                  :error="errors.password_confirmation"
                  @blur="validateField('password_confirmation', form.password_confirmation)"
                />
                <p v-if="errors.password_confirmation" class="mt-2 text-sm text-red-500">
                  {{ errors.password_confirmation }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </UCard>

      <UCard class="my-5">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-bold leading-6 text-poivre-800">
              Économies
            </h3>
            <p class="mt-1 text-sm text-poivre-500">
              Pour plus de précision et des économies personnalisées
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="grid grid-cols-6 gap-6">
              <div class="col-span-6">
                <label class="block text-base font-medium text-poivre-700 mb-2">
                  Commerces préférés
                </label>
                <div class="flex flex-wrap gap-2">
                  <label
                    v-for="store in stores"
                    v-show="store.base || showMoreStores"
                    :key="store.id"
                    class="inline-flex items-center px-2 py-2 border rounded-lg h-8 w-auto cursor-pointer"
                    :class="selectedStores.includes(store.slug) ? 'outline-1 outline-tomate border-tomate' : 'border-poivre-300'"
                  >
                    <UCheckbox
                      :model-value="selectedStores.includes(store.slug)"
                      :value="store.slug"
                      @update:model-value="(checked) => {
                        if (checked) {
                          selectedStores.push(store.slug)
                        }
                        else {
                          const index = selectedStores.indexOf(store.slug)
                          if (index > -1) {
                            selectedStores.splice(index, 1)
                          }
                        }
                        validateStores()
                      }"
                    />
                    <img
                      class="h-4.5 w-auto ml-2"
                      :src="`${config.public.backendCdnUrl}/u/stores/${store.id}.png`"
                      :alt="store.name"
                      loading="lazy"
                    >
                  </label>
                  <UButton
                    v-show="showMoreStores"
                    variant="ghost"
                    color="secondary"
                    size="md"
                    @click="showMoreStores = true"
                  >
                    Plus...
                  </UButton>
                </div>
                <p v-if="errors.stores" class="mt-2 text-sm text-red-500">
                  {{ errors.stores }}
                </p>
                <p class="mt-2 text-sm text-poivre-500">
                  On analyse chaque semaine les circulaires de 13 commerces parmi plus de 25 disponibles — choisis tes favoris!
                </p>
              </div>

              <div class="col-span-6 sm:col-span-6">
                <label for="postalcode" class="block text-base font-medium text-poivre-700">
                  Code postal
                </label>
                <UInput
                  v-model="form.postalcode"
                  type="text"
                  autocomplete="postal-code"
                  placeholder="A1A 1A1"
                  class="w-50"
                  size="lg"
                  :error="errors.postalcode"
                  @blur="validateField('postalcode', form.postalcode)"
                />
                <p v-if="errors.postalcode" class="mt-2 text-sm text-red-500">
                  {{ errors.postalcode }}
                </p>
                <p class="mt-2 text-sm text-poivre-500">
                  Utilisé pour géolocaliser les commerces à proximité.
                </p>
              </div>
            </div>
          </div>
        </div>
      </UCard>

      <UCard class="my-5">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-bold leading-6 text-poivre-800">
              Restons en contact
            </h3>
            <p class="mt-1 text-sm text-poivre-500">
              Suivez-nous sur les réseaux socieux et restez à l'affût.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="flex space-x-6">
              <a
                href="https://facebook.com/glouton.cuisine"
                target="_blank"
                title="Facebook"
                class="border-1 border-gray-200 rounded-lg p-2 items-center flex"
                @click="registerLink('facebook')"
              >
                <UIcon name="i-mdi-facebook" style="color:#1877f2" class="size-8" />
              </a>
              <a
                href="https://instagram.com/glouton.app"
                target="_blank"
                title="Instagram"
                class="border-1 border-gray-200 rounded-lg p-2 items-center flex"
                @click="registerLink('instagram')"
              >
                <UIcon name="i-mdi-instagram" style="color:#c32aa3" class="size-8" />
              </a>
              <a
                href="https://www.tiktok.com/@glouton.app"
                target="_blank"
                class="border-1 border-gray-200 rounded-lg p-2 items-center flex"
                @click="registerLink('tiktok')"
              >
                <UIcon name="i-ic-baseline-tiktok" style="color:#000000" class="size-8" title="TikTok" />
              </a>
            </div>
            <div class="mt-2 text-xs text-poivre-400">
              Une nouvelle fenêtre s'ouvrira en cliquant et vous ne perdrez pas votre inscription.
            </div>
          </div>
        </div>
      </UCard>

      <UCard class="my-5">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-bold leading-6 text-poivre-800">
              Préférences courriel
            </h3>
            <p class="mt-1 text-sm text-poivre-500">
              Quelles communications désirez-vous recevoir?
            </p>
          </div>
          <div class="md:col-span-2">
            <div class="mt-4 md:mt-0 space-y-4">
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <UCheckbox
                    id="email_alerts"
                    v-model="form.email_alerts"
                    :value="true"
                  />
                </div>
                <label for="email_alerts" class="ml-3 text-sm cursor-pointer">
                  <div class="font-medium text-poivre-700">
                    Résumé des meilleurs rabais des circulaires et communications
                  </div>
                  <p class="text-poivre-500">
                    1 fois par semaine
                  </p>
                </label>
              </div>
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <UCheckbox
                    id="email_intro"
                    v-model="form.email_intro"
                    :value="true"
                  />
                </div>
                <label for="email_intro" class="ml-3 text-sm cursor-pointer">
                  <div class="font-medium text-poivre-700">
                    Courriels d'introduction pour optimiser votre utilisation et vos économies
                  </div>
                  <p class="text-poivre-500">
                    Première semaine puis arrêt automatique (fortement recommandé)
                  </p>
                </label>
              </div>
            </div>
          </div>
        </div>
      </UCard>

      <UCard class="my-5">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-bold leading-6 text-poivre-800">
              Choisissez votre abonnement
            </h3>
            <p class="mt-1 text-sm text-poivre-500">
              L'Extra permet de maximiser ses économies et profiter de toutes les fonctionnalités sans limites.<br>
              <ULink class="text-tomate-400 underline font-semibold" :to="$localePath({ name: 'ExtraPage' })">
                Plus de détails...
              </ULink>
            </p>
          </div>
          <div class="mt-6 md:mt-0 md:col-span-2">
            <div class="grid grid-cols-1 gap-y-4 xl:grid-cols-2 sm:gap-x-4">
              <label
                v-for="plan in plans"
                :key="plan.id"
                class="relative bg-white rounded-lg border border-poivre-300 p-4 flex cursor-pointer overflow-hidden"
                :class="[selectedPlan === plan.type ? 'border-tomate-400 ring-2 ring-tomate-400' : 'hover:border-poivre-400']"
              >
                <input
                  v-model="selectedPlan"
                  type="radio"
                  :value="plan.type"
                  class="sr-only"
                >
                <div class="flex-1 flex">
                  <div class="flex flex-col w-full">
                    <span
                      class="block text-base text-poivre-600 font-medium"
                      :class="[plan.type === 'extra' ? 'text-tomate-400 font-extrabold' : '']"
                    >
                      <UIcon v-if="plan.type === 'extra'" name="i-mdi-star" class="inline align-text-top" style="color:#fcd349" />
                      {{ plan.title }}
                    </span>
                    <span class="mt-1 flex items-center text-sm text-poivre-500 leading-4" v-html="plan.description" />
                    <div class="mt-1 grid grid-cols-1 gap-y-1 gap-x-2 text-sm text-poivre-500">
                      <div v-for="feature in plan.features" :key="feature.id" class="flex flex-wrap items-center">
                        <UIcon v-if="feature.checked" name="i-mdi-check-bold" style="color:green" class="w-4" />
                        <UIcon v-else-if="feature.ads" name="i-mdi-broadcast" style="color:red" class="w-4" />
                        <UIcon v-else-if="feature.no_ads" name="i-mdi-broadcast-off" style="color:green" class="w-4" />
                        <UIcon v-else name="i-mdi-close-thick" style="color:red" />
                        <span class="ml-1 w-9/10">{{ feature.description }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <UIcon
                  class="h-6 w-6 text-tomate-400" :class="[selectedPlan !== plan.type ? 'invisible' : '']"
                  name="i-mdi-check-circle"
                  aria-hidden="true"
                />

                <div
                  v-if="plan.type === 'extra'"
                  class="py-1 leading-3 origin-top float-right mb-5 mr-14 w-40 text-center absolute bottom-0 right-0 text-poivre-800"
                  style="transform:translateX(50%) rotate(-45deg);background-color:gold"
                >
                  <div class="text-xs uppercase font-semibold">
                    14 jours gratuits
                  </div>
                  <div style="font-size:10px">
                    sans carte de crédit
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </UCard>

      <div class="flex flex-col items-end gap-2">
        <p class="text-xs text-right text-poivre-500">
          En créant un compte, vous acceptez les
          <ULink
            :to="$localePath({ name: 'TermsOfUsePage' })"
            target="_blank"
            class="underline inline-flex"
          >
            conditions d'utilisation
          </ULink>
          et la
          <ULink
            :to="$localePath({ name: 'PrivacyPolicyPage' })"
            target="_blank"
            class="underline inline-flex"
          >
            politique de confidentialité
          </ULink>
          de Glouton.
        </p>
        <UButton
          type="submit"
          color="primary"
          size="lg"
          :loading="isRegisterLoading"
          :disabled="isRegisterLoading"
          :label="isRegisterLoading ? 'Veuillez patienter...' : 'Créer mon compte'"
        />
      </div>
    </form>
  </main>
</template>

<style scoped>
/* Custom styles if needed */
</style>
